import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { ApiService } from '../api/api.service';
import { API_CONFIG } from '../config/api.config';

export interface DashboardProject {
    id: string | null;
    name: string;
    status: string;
    propertyCount: number;
    imageUrl?: string | null;
}

export interface DashboardProperty {
    id: string | null;
    title: string;
    status: string;
    price: number | null;
    currencyCode?: string | null;
    unitNumber?: string | null;
    projectName?: string | null;
    imageUrl?: string | null;
    imageThumb?: string | null;
}

export interface DashboardReservation {
    id: string | null;
    propertyId: string | null;
    propertyLabel: string | null;
    customerName: string | null;
    customerEmail?: string | null;
    customerPhone?: string | null;
    status: string | null;
    amount?: number | null;
    installmentAmount?: number | null;
    currency?: { code?: string | null; symbol?: string | null } | null;
    createdTime?: string | null;
}

export interface DashboardResponse {
    viewer?: {
        property_access_scope?: 'assigned' | 'projects' | 'all' | string;
    };
    projects: { total: number; items: DashboardProject[] };
    properties: {
        total: number;
        statusCounts: Record<string, number>;
        previousStatusCounts?: Record<string, number>;
        statusTrends?: Record<string, number[]>;
        totalAmount?: number;
        previousTotalAmount?: number;
        statusAmounts?: Record<string, number>;
        totalAmountsByCurrency?: Record<string, number>;
        totalAreaM2?: number;
        totalAmountsByCurrencyWithArea?: Record<string, number>;
        statusAmountsByCurrency?: Record<string, Record<string, number>>;
        previousTotalAmountsByCurrency?: Record<string, number>;
        previousStatusAmountsByCurrency?: Record<string, Record<string, number>>;
        items: DashboardProperty[];
    };
    reservations: { total: number; items: DashboardReservation[] };
    subpartners?: {
        assignedProperties: number;
        assignments: number;
        avgCommissionPercent?: number | null;
        totalValueByCurrency?: Record<string, number>;
        projectedCommissionByCurrency?: Record<string, number>;
        totalAreaM2?: number;
    };
    salesTeam?: {
        assignedProperties: number;
        assignments: number;
        avgCommissionPercent?: number | null;
        totalValueByCurrency?: Record<string, number>;
        projectedCommissionByCurrency?: Record<string, number>;
        totalAreaM2?: number;
    };
    portfolio?: {
        assignedProperties: number;
        assignments: number;
        avgCommissionPercent?: number | null;
        totalValueByCurrency?: Record<string, number>;
        projectedCommissionByCurrency?: Record<string, number>;
        totalAreaM2?: number;
    };
}

@Injectable({ providedIn: 'root' })
export class DashboardService {
    private api = inject(ApiService);
    private http = inject(HttpClient);
    private readonly PATH = '/API2/dashboard';

    getDashboard(options: { period?: string; from?: string; to?: string; projectId?: string | null } = {}): Observable<DashboardResponse> {
        let params = new HttpParams();
        if (options.period) params = params.set('period', options.period);
        if (options.from) params = params.set('from', options.from);
        if (options.to) params = params.set('to', options.to);
        if (options.projectId) params = params.set('project_id', options.projectId);
        return this.api.get<any>(this.PATH, params).pipe(
            map((resp) => resp?.data || resp || {}),
            map((data) => ({
                viewer: data.viewer || undefined,
                projects: data.projects || { total: 0, items: [] },
                properties: data.properties || { total: 0, statusCounts: {}, items: [] },
                reservations: data.reservations || { total: 0, items: [] },
                subpartners: data.subpartners || undefined,
                salesTeam: data.salesTeam || undefined,
                portfolio: data.portfolio || undefined
            }))
        );
    }

    getPartnerPerformanceSummary(dateRange: '7d' | '30d' | 'ytd' | 'all' = 'ytd', partnerId?: string | null): Observable<any> {
        let params = new HttpParams().set('date_range', dateRange);
        if (partnerId) params = params.set('partner_id', partnerId);
        return this.api.get<any>('/API2/partners/performance/summary', params).pipe(
            map((resp) => resp || {})
        );
    }

    exportPartnerPerformance(dateRange: '7d' | '30d' | 'ytd' | 'all' = 'ytd', partnerId?: string | null): Observable<Blob> {
        let params = new HttpParams().set('date_range', dateRange);
        if (partnerId) params = params.set('partner_id', partnerId);
        const url = `${API_CONFIG.API2_URL}/partners/performance/export`;
        return this.http.get(url, { params, responseType: 'blob' });
    }
}
